import Script from 'next/script';
import { useEffect, useState } from 'react';

import { KANG_WIDGET_TOGGLE } from '@/constants/feature-toggles/kang-widget';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { useKangTicket } from '@/hooks/use-kang-ticket';
import { runAfterLoadWhenIdle } from '@/utils/run-after-load-when-idle';

const KANG_SCRIPT_SRC = 'https://kang.noping.com/kang.js';

/**
 * O toggle é o único portão de *se*: ligado, o Kang é o canal de atendimento e o lançador do
 * Intercom deixa de ser renderizado (`intercom-launcher.tsx`). Qualquer condição extra que pudesse
 * responder "não" tiraria a bolha de um grupo de visitantes sem devolver o Intercom para eles — ou
 * seja, deixaria gente sem canal nenhum. Nada aqui faz isso.
 *
 * `load` + idle é portão de *quando*, e só. A cadeia inteira saía durante a hidratação e cabia na
 * janela que o Lighthouse cobra do LCP: no trace de /pt mobile da rodada 1 são seis idas e voltas —
 * preflight de `feature-toggle/check` em 774 ms, o fetch em 891 ms, `kang.js` em 967 ms,
 * `api/widget/versao` em 1065 ms, o bundle de 26 KB em 1127 ms e `api/presenca` em 1178 ms —
 * todas antes da pintura observada em 1397 ms. Nada da primeira tela depende de uma bolha de
 * suporte, e ninguém clica nela no primeiro segundo; o atraso é de ~1 s e a bolha continua
 * aparecendo em toda visita, que é o que não pode mudar.
 *
 * `autoFetch` é o que carrega o gate: é a chamada do toggle que abre a cadeia, então adiar só o
 * `<Script>` deixaria o primeiro round-trip dentro da janela. A espera pelo bilhete continua sendo
 * a única pausa depois disso, e `useKangTicket` a limita no tempo a partir do novo início.
 */
export default function KangWidget() {
  const [isAfterLoadIdle, setIsAfterLoadIdle] = useState(false);

  useEffect(() => runAfterLoadWhenIdle(() => setIsAfterLoadIdle(true)), []);

  const { isEnabled } = useFeatureToggle({ key: KANG_WIDGET_TOGGLE, autoFetch: isAfterLoadIdle });
  const ticketState = useKangTicket(isEnabled);

  if (!isEnabled || ticketState !== 'ready') return null;

  return <Script id='kang-widget' src={KANG_SCRIPT_SRC} strategy='afterInteractive' />;
}
